import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageLayout } from "../../components/PageLayout";
import { Alert, AlertDescription, Card, EmptyState, Link, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { ADMIN_CHART_COLORS, ActivityBarChart, AdminStatTile, RangePicker, rangeToDates, useAdminFetch } from "../../components/admin";
import type { RangeKey } from "../../components/admin";
import { formatCount, formatRelative } from "../../utils/adminFormat";
import type { ActivitySummary, LeadRow, OrgActivityRow, SeriesPoint } from "../../types/admin";

type ChartCardProps = {
  icon: string;
  iconColorClass: string;
  title: string;
  total: number | null;
  data: SeriesPoint[];
  color: string;
  valueLabel: string;
  isLoading: boolean;
};

const ChartCard = ({ icon, iconColorClass, title, total, data, color, valueLabel, isLoading }: ChartCardProps) => (
  <Card className="p-4">
    <View className="flex flex-row items-center justify-between mb-3">
      <View className="flex flex-row items-center gap-1.5">
        <Icon
          name={icon}
          size={14}
          className={iconColorClass}
        />
        <Text className="text-sm font-medium text-gray-900 dark:text-white">{title}</Text>
      </View>
      {total !== null && <Text className="text-sm tabular-nums text-gray-400 dark:text-gray-500">{formatCount(total)}</Text>}
    </View>
    {isLoading ? (
      <div className="h-[180px] animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
    ) : (
      <ActivityBarChart
        data={data}
        color={color}
        valueLabel={valueLabel}
      />
    )}
  </Card>
);

export const AdminOverviewPage = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeKey>("30d");

  const qs = useMemo(() => {
    const { startDate, endDate } = rangeToDates(range);
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [range]);

  const {
    data: summaryResponse,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useAdminFetch<{ data: ActivitySummary }>(`/v1/admin/activity/summary?${qs}`);
  const {
    data: orgsResponse,
    isLoading: isOrgsLoading,
    error: orgsError,
  } = useAdminFetch<{ data: OrgActivityRow[] }>(`/v1/admin/activity/organizations?${qs}`);
  const { data: leadsResponse, isLoading: isLeadsLoading, error: leadsError } = useAdminFetch<{ data: LeadRow[] }>("/v1/leads?limit=5");

  const summary = summaryResponse?.data ?? null;
  const latestLeads = leadsResponse?.data ?? [];

  const leaderboard = useMemo(
    () =>
      (orgsResponse?.data ?? [])
        .filter((org) => org.sessions > 0)
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 10),
    [orgsResponse],
  );
  const maxSessions = leaderboard[0]?.sessions ?? 0;

  return (
    <PageLayout
      title="Platform overview"
      description="Activity across leads, users, and organizations."
      headerAction={
        <RangePicker
          value={range}
          onChange={setRange}
        />
      }
    >
      <View className="mt-6 space-y-4">
        {(summaryError ?? orgsError ?? leadsError) && (
          <Alert variant="destructive">
            <AlertDescription>{summaryError ?? orgsError ?? leadsError}</AlertDescription>
          </Alert>
        )}
        <View className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <AdminStatTile
            icon="person"
            iconColorClass="text-violet-500"
            label="Users"
            value={summary ? formatCount(summary.totals.users) : "—"}
            delta={summary ? `+${formatCount(summary.period.newUsers)} in period` : null}
            isLoading={isSummaryLoading}
          />
          <AdminStatTile
            icon="users"
            iconColorClass="text-emerald-600"
            label="Organizations"
            value={summary ? formatCount(summary.totals.organizations) : "—"}
            delta={summary ? `+${formatCount(summary.period.newOrganizations)} in period` : null}
            isLoading={isSummaryLoading}
          />
          <AdminStatTile
            icon="target"
            iconColorClass="text-amber-500"
            label="Leads"
            value={summary ? formatCount(summary.totals.leads) : "—"}
            delta={summary ? `+${formatCount(summary.period.newLeads)} in period` : null}
            isLoading={isSummaryLoading}
          />
          <AdminStatTile
            icon="bot"
            iconColorClass="text-blue-500"
            label="Agents"
            value={summary ? formatCount(summary.totals.agents) : "—"}
            delta={summary ? `+${formatCount(summary.period.newAgents)} in period` : null}
            isLoading={isSummaryLoading}
          />
          <AdminStatTile
            icon="phone"
            iconColorClass="text-cyan-600"
            label={`Sessions (${range})`}
            value={summary ? formatCount(summary.period.sessions) : "—"}
            delta={null}
            isLoading={isSummaryLoading}
          />
          <AdminStatTile
            icon="rocket"
            iconColorClass="text-rose-500"
            label="Releases"
            value={summary ? formatCount(summary.period.releases) : "—"}
            delta={null}
            isLoading={isSummaryLoading}
          />
        </View>

        <View className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            icon="person"
            iconColorClass="text-violet-500"
            title="New signups"
            total={summary ? summary.period.newUsers : null}
            data={summary?.series.signups ?? []}
            color={ADMIN_CHART_COLORS.signups}
            valueLabel="signups"
            isLoading={isSummaryLoading}
          />
          <ChartCard
            icon="target"
            iconColorClass="text-amber-500"
            title="New leads"
            total={summary ? summary.period.newLeads : null}
            data={summary?.series.leads ?? []}
            color={ADMIN_CHART_COLORS.leads}
            valueLabel="leads"
            isLoading={isSummaryLoading}
          />
          <ChartCard
            icon="phone"
            iconColorClass="text-cyan-600"
            title="Sessions"
            total={summary ? summary.period.sessions : null}
            data={summary?.series.sessions ?? []}
            color={ADMIN_CHART_COLORS.sessions}
            valueLabel="sessions"
            isLoading={isSummaryLoading}
          />
          <ChartCard
            icon="users"
            iconColorClass="text-emerald-600"
            title="New organizations"
            total={summary ? summary.period.newOrganizations : null}
            data={summary?.series.newOrganizations ?? []}
            color={ADMIN_CHART_COLORS.organizations}
            valueLabel="organizations"
            isLoading={isSummaryLoading}
          />
        </View>

        <View className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="p-4 xl:col-span-2">
            <View className="flex flex-row items-center gap-1.5 mb-3">
              <Icon
                name="trending-up"
                size={14}
                className="text-purple-500"
              />
              <Text className="text-sm font-medium text-gray-900 dark:text-white">Most engaged organizations</Text>
            </View>
            {isOrgsLoading ? (
              <View className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-7 animate-pulse rounded bg-gray-100 dark:bg-gray-800"
                  />
                ))}
              </View>
            ) : leaderboard.length === 0 ? (
              <EmptyState
                icon="activity"
                title="No session activity"
                description="No organizations recorded sessions in this period."
                className="!border-0 !shadow-none"
              />
            ) : (
              <View className="space-y-1">
                {leaderboard.map((org, index) => (
                  <div
                    key={org.organizationId}
                    onClick={() => navigate(`/admin/organizations?id=${org.organizationId}`)}
                    className="flex flex-row items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <Text className="w-6 shrink-0 text-xs text-gray-400 tabular-nums">{index + 1}</Text>
                    <Text className="w-44 shrink-0 text-sm font-medium text-gray-900 dark:text-white truncate">{org.name}</Text>
                    <View className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <div
                        className="h-1.5 rounded-full bg-cyan-600 dark:bg-cyan-500"
                        style={{ width: `${(org.sessions / maxSessions) * 100}%` }}
                      />
                    </View>
                    <Text className="w-14 shrink-0 text-right text-sm font-medium tabular-nums text-gray-900 dark:text-white">{formatCount(org.sessions)}</Text>
                    <Text className="hidden sm:block w-44 shrink-0 text-right text-xs text-gray-400 dark:text-gray-500 truncate">
                      {formatCount(org.releases)} releases · {formatCount(org.agentsCreated)} agents
                    </Text>
                  </div>
                ))}
              </View>
            )}
          </Card>

          <Card className="p-4">
            <View className="flex flex-row items-center justify-between mb-3">
              <View className="flex flex-row items-center gap-1.5">
                <Icon
                  name="target"
                  size={14}
                  className="text-amber-500"
                />
                <Text className="text-sm font-medium text-gray-900 dark:text-white">Latest leads</Text>
              </View>
              <Link
                to="/admin/leads"
                className="text-xs"
              >
                View all
              </Link>
            </View>
            {isLeadsLoading ? (
              <View className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-9 animate-pulse rounded bg-gray-100 dark:bg-gray-800"
                  />
                ))}
              </View>
            ) : latestLeads.length === 0 ? (
              <Text className="block py-6 text-center text-sm text-gray-400 dark:text-gray-500">No leads yet</Text>
            ) : (
              <View className="divide-y divide-gray-100 dark:divide-gray-800">
                {latestLeads.map((lead) => (
                  <View
                    key={lead.id}
                    className="flex flex-row items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <View className="min-w-0">
                      <Text className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                        {lead.firstName} {lead.lastName}
                      </Text>
                      {lead.company && <Text className="block text-xs text-gray-400 dark:text-gray-500 truncate">{lead.company}</Text>}
                    </View>
                    <Text className="shrink-0 text-xs text-gray-400 dark:text-gray-500">{formatRelative(lead.createdAt)}</Text>
                  </View>
                ))}
              </View>
            )}
          </Card>
        </View>
      </View>
    </PageLayout>
  );
};
